/**
 * DonorComms.jsx — Phase 4
 * Communication log for donor interactions: emails, calls, meetings, notes.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  getDonorComms, createDonorComm, updateDonorComm, deleteDonorComm,
  getDonors, getDonorContacts, COMM_TYPES, COMM_DIRECTIONS, fmtDate, fmtDateTime,
} from '../../../api/donorsApi';

// ── Type icon ─────────────────────────────────────────────────────────────────
const TYPE_ICONS = { email: '✉️', call: '📞', meeting: '🤝', note: '📝' };

function CommTypeBadge({ type }) {
  const t = COMM_TYPES.find(x => x.value === type) || { label: type };
  return (
    <span className="bb-dn-type-pill">
      {TYPE_ICONS[type] || '💬'} {t.label}
    </span>
  );
}

// ── Comm Form Modal ────────────────────────────────────────────────────────────
function CommFormModal({ comm, orgId, donors, contacts, onClose, onSaved }) {
  const isEdit = !!comm;
  const [form, setForm] = useState({
    donorId:   comm?.donorId   || (donors[0]?.id || ''),
    contactId: comm?.contactId || '',
    type:      comm?.type      || 'note',
    direction: comm?.direction || 'outbound',
    subject:   comm?.subject   || '',
    body:      comm?.body      || '',
    sentAt:    comm?.sentAt    || new Date().toISOString().slice(0, 16),
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Contacts for selected donor
  const filteredContacts = contacts.filter(c => c.donorId === form.donorId);

  const handleSave = async () => {
    if (!form.subject.trim()) { setError('Subject / summary is required.'); return; }
    if (!form.donorId)        { setError('Please select a donor.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, sentAt: form.sentAt || null };
      if (isEdit) await updateDonorComm(comm.id, payload);
      else         await createDonorComm(orgId, payload);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-dn-modal bb-dn-modal--wide">
        <div className="bb-dn-modal-header">
          <h3>{isEdit ? 'Edit Communication' : 'Log Communication'}</h3>
          <button className="bb-dn-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="bb-dn-alert bb-dn-alert--error">{error}</div>}

        <div className="bb-dn-form-grid">
          <div className="bb-dn-form-group">
            <label>Donor <span className="bb-dn-required">*</span></label>
            <select className="bb-dn-select" value={form.donorId} onChange={e => { set('donorId', e.target.value); set('contactId', ''); }}>
              <option value="">— Select donor —</option>
              {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group">
            <label>Contact (optional)</label>
            <select className="bb-dn-select" value={form.contactId} onChange={e => set('contactId', e.target.value)}>
              <option value="">— Select contact —</option>
              {filteredContacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group">
            <label>Type</label>
            <select className="bb-dn-select" value={form.type} onChange={e => set('type', e.target.value)}>
              {COMM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group">
            <label>Direction</label>
            <select className="bb-dn-select" value={form.direction} onChange={e => set('direction', e.target.value)}>
              {COMM_DIRECTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group">
            <label>Date & Time</label>
            <input className="bb-dn-input" type="datetime-local" value={form.sentAt} onChange={e => set('sentAt', e.target.value)} />
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Subject / Summary <span className="bb-dn-required">*</span></label>
            <input className="bb-dn-input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Brief subject or summary of the interaction" />
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Details / Notes</label>
            <textarea className="bb-dn-textarea" rows={5} value={form.body} onChange={e => set('body', e.target.value)} placeholder="Full details, action items, follow-ups…" />
          </div>
        </div>

        <div className="bb-dn-modal-footer">
          <button className="bb-dn-btn bb-dn-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="bb-dn-btn bb-dn-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Log Communication'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Comm Entry ────────────────────────────────────────────────────────────────
function CommEntry({ comm, donorName, contactName, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bb-dn-comm-entry">
      <div className="bb-dn-comm-icon">{TYPE_ICONS[comm.type] || '💬'}</div>
      <div className="bb-dn-comm-content">
        <div className="bb-dn-comm-header">
          <span className="bb-dn-comm-subject">{comm.subject}</span>
          <CommTypeBadge type={comm.type} />
          <span className="bb-dn-badge" style={{ background: '#F3F4F6', color: '#6B7280' }}>
            {comm.direction === 'inbound' ? '← Inbound' : '→ Outbound'}
          </span>
        </div>
        <div className="bb-dn-comm-meta">
          <span>🏢 {donorName}</span>
          {contactName && <span>👤 {contactName}</span>}
          <span>🕐 {comm.sentAt ? fmtDateTime({ toDate: () => new Date(comm.sentAt) }) : fmtDate(comm.createdAt)}</span>
        </div>
        {comm.body && (
          <>
            <div className="bb-dn-comm-body" style={{ WebkitLineClamp: expanded ? 'unset' : 2, display: expanded ? 'block' : '-webkit-box' }}>
              {comm.body}
            </div>
            {comm.body.length > 120 && (
              <button className="bb-dn-btn-link" onClick={() => setExpanded(v => !v)}>
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>
      <div className="bb-dn-comm-actions">
        <button className="bb-dn-action-btn" onClick={onEdit}>Edit</button>
        <button className="bb-dn-action-btn bb-dn-action-btn--danger" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DonorComms({ orgId, filterDonorId = null }) {
  const [comms,    setComms]    = useState([]);
  const [donors,   setDonors]   = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [donorFilter,  setDonorFilter]  = useState(filterDonorId || 'all');
  const [showForm,  setShowForm]  = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d, ct] = await Promise.all([
        getDonorComms(orgId, filterDonorId),
        getDonors(orgId),
        getDonorContacts(orgId),
      ]);
      setComms(c); setDonors(d); setContacts(ct);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [orgId, filterDonorId]);

  useEffect(() => { load(); }, [load]);

  const filtered = comms.filter(c => {
    const matchType  = typeFilter  === 'all' || c.type    === typeFilter;
    const matchDonor = donorFilter === 'all' || c.donorId === donorFilter;
    return matchType && matchDonor;
  });

  const getDonorName  = id => donors.find(d => d.id === id)?.name   || '—';
  const getContactName= id => contacts.find(c => c.id === id)?.name || '';

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteDonorComm(deleteItem.id); setDeleteItem(null); load(); }
    catch (e) { console.error(e); }
    finally { setDeleting(false); }
  };

  if (loading) return <div className="bb-dn-loading"><div className="bb-loading-spinner" /><p>Loading communications…</p></div>;

  return (
    <div className="bb-dn-section">
      <div className="bb-dn-section-header">
        <div>
          <h2 className="bb-dn-section-title">Communications Log</h2>
          <p className="bb-dn-section-subtitle">Record emails, calls, meetings and notes with donors</p>
        </div>
        <button className="bb-dn-btn bb-dn-btn-primary" onClick={() => { setEditItem(null); setShowForm(true); }}>
          + Log Communication
        </button>
      </div>

      <div className="bb-dn-filters">
        {!filterDonorId && (
          <select className="bb-dn-select" value={donorFilter} onChange={e => setDonorFilter(e.target.value)}>
            <option value="all">All Donors</option>
            {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        <select className="bb-dn-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {COMM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <span className="bb-dn-count">{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bb-dn-empty">
          <div className="bb-dn-empty-icon">💬</div>
          <p>{comms.length === 0 ? 'No communications logged yet.' : 'No entries match your filters.'}</p>
        </div>
      ) : (
        <div className="bb-dn-comm-list">
          {filtered.map(c => (
            <CommEntry
              key={c.id}
              comm={c}
              donorName={getDonorName(c.donorId)}
              contactName={getContactName(c.contactId)}
              onEdit={() => { setEditItem(c); setShowForm(true); }}
              onDelete={() => setDeleteItem(c)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <CommFormModal
          comm={editItem}
          orgId={orgId}
          donors={donors}
          contacts={contacts}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={() => { setShowForm(false); setEditItem(null); load(); }}
        />
      )}

      {deleteItem && (
        <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteItem(null)}>
          <div className="bb-dn-modal" style={{ maxWidth: 440 }}>
            <div className="bb-dn-modal-header">
              <h3>Delete Communication</h3>
              <button className="bb-dn-modal-close" onClick={() => setDeleteItem(null)}>✕</button>
            </div>
            <p style={{ padding: '16px 0', color: 'var(--bb-gray)' }}>Delete this communication log entry?</p>
            <div className="bb-dn-modal-footer">
              <button className="bb-dn-btn bb-dn-btn-secondary" onClick={() => setDeleteItem(null)}>Cancel</button>
              <button className="bb-dn-btn bb-dn-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
